import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { DefaultFailureAction, dataHookFactory, initMapFactory } from '../common/reducers/utilities';
import { ensureArray } from '../common/utils/arrayUtilities';

const compareJobForm = (f1, f2) => f1.id === f2.id;

const jobCustomFormSlice = createSlice({
    name: 'jobCustomForm',
    initialState: {
        defs: [],
        jobForms: {}
    },
    reducers: {
        setDefs: (state, action) => {
            state.defs = action.payload;
        },
        addOrUpdateFormDef: (state, action) => {
            for (const def of ensureArray(action.payload)) {
                const idx = state.defs.findIndex(d => d.id === def.id);
                if (idx > -1) {
                    state.defs.splice(idx, 1, def);
                } else {
                    state.defs.push(def);
                }
            }
        },
        removeFormDef: (state, action) => {
            for (const def of ensureArray(action.payload)) {
                const idx = state.defs.findIndex(d => d.id === def.id);
                if (idx > -1) {
                    state.defs.splice(idx, 1);
                }
            }
        },
        setJobForms: (state, action) => {
            const forms = ensureArray(action.payload);
            for (const form of forms) {
                if (state.jobForms[form.jobid?.toString()]) {
                    delete state.jobForms[form.jobid?.toString()];
                }
            }
            for (const form of forms) {
                const jobid = form.jobid?.toString();
                if (!state.jobForms[jobid]) {
                    state.jobForms[jobid] = []
                }
                state.jobForms[jobid].push(form);
            }
        },
        addOrUpdateForm: (state, action) => {
            for (const form of ensureArray(action.payload)) {
                const jobid = form.jobid?.toString();
                if (state.jobForms[jobid]) {
                    let idx = state.jobForms[jobid].findIndex(f => compareJobForm(form, f));
                    if (idx >= 0) {
                        state.jobForms[jobid].splice(idx, 1, form);
                    } else {
                        state.jobForms[jobid].push(form);
                    }
                } else {
                    state.jobForms[jobid] = [form];
                }
            }
        },
        removeForm: (state, action) => {
            for (const form of ensureArray(action.payload)) {
                const jobid = form.jobid?.toString();
                if (state.jobForms[jobid]) {
                    let idx = state.jobForms[jobid].findIndex(f => compareJobForm(form, f));
                    if (idx >= 0) {
                        state.jobForms[jobid].splice(idx, 1);
                    }
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => jobCustomFormSlice.getInitialState());
    }
});

// #region network operations
const visualizeFailure = verb => {
    const failureAction = DefaultFailureAction(verb, 'jobForms');
    return (data, meta, tools) => {
        if (data.status === 413) {
            tools.showError(tools.t('jobFormPayloadTooLargeMessage'), tools.t('defaultNetworkErrorDetails', data), { timestamp: data.timestamp });
            return;
        }
        return failureAction(data, meta, tools);
    };
};

const fetchDefs = network => network.get('/api/v1/customFormJobDefs', jobCustomFormSlice.actions.setDefs);
export const fetchJobForms = jobid => network => network.get('/api/v1/customJobFormSearch/' + encodeURIComponent(jobid), jobCustomFormSlice.actions.setJobForms);
export const insertJobForm = (network, form) => network.post('/api/v1/customJobForm', form, jobCustomFormSlice.actions.addOrUpdateForm, { failure: visualizeFailure('create') });
export const updateJobForm = (network, form) => network.put('/api/v1/customJobForm', form, jobCustomFormSlice.actions.addOrUpdateForm, { failure: visualizeFailure('update') });
export const insertJobFormDef = (network, def) => network.post('/api/v1/customFormJobDef', def, jobCustomFormSlice.actions.addOrUpdateFormDef);
export const updateJobFormDef = (network, def) => network.put('/api/v1/customFormJobDef', def, jobCustomFormSlice.actions.addOrUpdateFormDef);
export const removeJobFormDef = (network, def) => network.delete('/api/v1/customFormJobDef/' + encodeURIComponent(def.id), jobCustomFormSlice.actions.removeFormDef);
// #endregion

// #region selectors
const selectJobFormDefs = state => state.jobCustomForm.defs;
const selectForms = state => state.jobCustomForm.jobForms;
const selectJobFormsByJobID = createSelector([selectForms, (forms, jobid) => jobid], (forms, jobid) => forms[jobid?.toString()] ?? []);
const selectJobForms = jobid => state => selectJobFormsByJobID(state, jobid);
// #endregion

// #region permissions
export const canCreateJobFormDef = (credentials, def) => credentials.access.hasFull('administrator');
export const canUpdateJobFormDef = canCreateJobFormDef;
export const canRemoveJobFormDef = canCreateJobFormDef;
// #endregion

// #region listeners
const jobFormDefListen = {
    jobFormDefCreated: (def, { dispatch }) => dispatch(jobCustomFormSlice.actions.addOrUpdateFormDef(def)),
    jobFormDefUpdated: (def, { dispatch }) => dispatch(jobCustomFormSlice.actions.addOrUpdateFormDef(def)),
    jobFormDefRemoved: (def, { dispatch }) => dispatch(jobCustomFormSlice.actions.removeFormDef(def))
}
const jobFormListen = {
    jobFormCreated: (form, { dispatch }) => dispatch(jobCustomFormSlice.actions.addOrUpdateForm(form)),
    jobFormUpdated: (form, { dispatch }) => dispatch(jobCustomFormSlice.actions.addOrUpdateForm(form))
}
// #endregion

export const [useJobFormDefs] = dataHookFactory(selectJobFormDefs, { run: fetchDefs, listen: jobFormDefListen });
export const [useJobForms,realizeJobForms] = dataHookFactory(selectJobForms, initMapFactory(fetchJobForms, jobFormListen));

const reducerDetails = { name : jobCustomFormSlice.name, reducer: jobCustomFormSlice.reducer };
export default reducerDetails;